/**
 * Build-time configuration (dev.md §5: `VITE_`-prefixed, and only wiring).
 *
 * Two things here are decisions rather than plumbing.
 *
 * **The API base is empty in development.** `core-api` mounts no CORS
 * middleware — deliberately, it is a same-origin service behind the load
 * balancer — so a browser on `:5173` talking to `:8081` would be refused by the
 * preflight. Vite's dev server proxies `/v1` and `/healthz` instead, which makes
 * the development origin the same origin, which is the shape production has. A
 * built bundle uses `VITE_CORE_API_URL`.
 *
 * **`reporting-api` is a second base URL, not a second app.** The analytics
 * section reads `GET /v1/reports*` from a *different service* (dev.md §5's
 * `reporting-api` block — its own pool, its own read replica), and that service
 * happens to share a path prefix with `core-api`'s six MVP-4 reports. The two
 * sets do not collide route-for-route (`/v1/reports/revenue` is the replica's,
 * `/v1/reports/revenue-by-month` is `core-api`'s) but they are one prefix, so
 * only a path-aware edge can serve both from one origin. That is a deployment
 * decision rather than a bundle decision, so the bundle takes a URL:
 * `VITE_REPORTING_API_URL`, `''` meaning same-origin exactly as
 * `VITE_CORE_API_URL` does. Development does not guess at it either — it proxies
 * a distinct prefix (`/reporting`) so the dev origin has the shape a routed
 * production origin would.
 *
 * **`VITE_AUTH_FAKE` is a development affordance and says so.** It mints the
 * unsigned token shape `core-api`'s `FakeVerifier` accepts, which that class
 * refuses to honour when `NODE_ENV=production`. The app carries the same
 * asymmetry: the flag is read once, here, and the sign-in screen states plainly
 * which mode it is in rather than looking identical to the real one.
 */

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
}

export interface AppConfig {
  /** `''` means same-origin (the dev proxy, or Hosting rewrites). */
  apiBaseUrl: string;
  /**
   * Where `reporting-api` lives. `''` means same-origin, which only works if
   * the edge routes the replica's five report paths away from `core-api`.
   */
  reportingApiBaseUrl: string;
  auth: { mode: 'firebase'; firebase: FirebaseConfig } | { mode: 'fake'; tenantId: string };
  /** The mirrored window of the book (spec §8.1: "today plus a window"). */
  mirrorPastDays: number;
  mirrorFutureDays: number;
  /**
   * `core-api` **clamps** a calendar window to `CALENDAR_MAX_WINDOW_DAYS`
   * (default 31) rather than rejecting it, so asking for 68 days silently
   * returns 31. The mirror therefore pages, and the page has to stay under the
   * server's clamp or the tail of the window would never arrive.
   */
  calendarChunkDays: number;
}

interface RawEnv {
  VITE_CORE_API_URL?: string | undefined;
  VITE_REPORTING_API_URL?: string | undefined;
  VITE_FIREBASE_API_KEY?: string | undefined;
  VITE_FIREBASE_AUTH_DOMAIN?: string | undefined;
  VITE_FIREBASE_PROJECT_ID?: string | undefined;
  VITE_FIREBASE_APP_ID?: string | undefined;
  VITE_AUTH_FAKE?: string | undefined;
  VITE_AUTH_FAKE_TENANT?: string | undefined;
  DEV?: boolean | undefined;
}

export function readConfig(env: RawEnv): AppConfig {
  const fake = env.VITE_AUTH_FAKE === '1' || env.VITE_AUTH_FAKE === 'true';

  return {
    apiBaseUrl: env.DEV === true ? '' : trimSlash(env.VITE_CORE_API_URL ?? ''),
    // `/reporting` in development is the Vite proxy's prefix (see
    // `vite.config.ts`), stripped before the request leaves for :8083. It is a
    // *prefix* rather than an origin for the same reason `apiBaseUrl` is empty:
    // the dev origin should have the shape the deployed one has.
    reportingApiBaseUrl:
      env.DEV === true ? '/reporting' : trimSlash(env.VITE_REPORTING_API_URL ?? ''),
    auth: fake
      ? { mode: 'fake', tenantId: env.VITE_AUTH_FAKE_TENANT ?? 't_test_conformance' }
      : {
          mode: 'firebase',
          firebase: {
            apiKey: env.VITE_FIREBASE_API_KEY ?? '',
            authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
            projectId: env.VITE_FIREBASE_PROJECT_ID ?? '',
            appId: env.VITE_FIREBASE_APP_ID ?? '',
          },
        },
    mirrorPastDays: 7,
    mirrorFutureDays: 60,
    calendarChunkDays: 28,
  };
}

function trimSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export const config: AppConfig = readConfig(import.meta.env as unknown as RawEnv);
